import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/Nav";
import { getToken } from "../lib/auth";

function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(path, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: `Bearer ${token}`,
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
    },
  });
}

export default function Chat() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  // Load conversation list
  useEffect(() => {
    apiFetch("/api/chat/conversations")
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { setConversations(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Load messages when active convo changes
  useEffect(() => {
    if (!activeConvo) return;
    setMsgLoading(true);
    apiFetch(`/api/chat/messages?conversation_id=${activeConvo.id}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { setMessages(Array.isArray(data) ? data : []); setMsgLoading(false); })
      .catch(() => setMsgLoading(false));
  }, [activeConvo]);

  // Scroll to bottom when messages load
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!text.trim() || !activeConvo) return;
    setSending(true);
    try {
      const r = await apiFetch("/api/chat/messages", {
        method: "POST",
        body: JSON.stringify({ conversation_id: activeConvo.id, text }),
      });
      if (r.ok) {
        const msg = await r.json();
        setMessages((prev) => [...prev, msg]);
        setText("");
        setConversations((prev) =>
          prev.map((c) => c.id === activeConvo.id ? { ...c, last_message: text } : c)
        );
      }
    } finally {
      setSending(false);
    }
  };

  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem("bm_auth_user"))?.id; } catch { return null; }
  })();

  // ── Thread view ──────────────────────────────────────────────────────────────
  if (activeConvo) {
    return (
      <AppShell>
        <div className="flex flex-col h-[calc(100vh-64px)]">
          {/* Header */}
          <div className="flex items-center gap-4 px-6 py-4 border-b border-white/10">
            <button onClick={() => setActiveConvo(null)} className="text-white/50 hover:text-white text-sm">
              ← Back
            </button>
            {activeConvo.image && (
              <img src={activeConvo.image} alt="" className="h-9 w-9 rounded-full object-cover" />
            )}
            <div className="font-display text-lg">{activeConvo.name}</div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-3">
            {msgLoading && <div className="text-white/40 text-sm">Loading…</div>}
            {!msgLoading && messages.length === 0 && (
              <div className="text-center text-white/35 text-sm pt-10">
                No messages yet. Say hello.
              </div>
            )}
            {messages.map((m) => {
              const mine = m.sender_id === currentUserId;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[72%] rounded-2xl px-4 py-2.5 text-sm ${
                      mine
                        ? "bg-gradient-gold text-[#05070d] rounded-br-sm"
                        : "glass text-white rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-6 py-4 border-t border-white/10 flex gap-3">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message…"
              className="flex-1 bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/30"
            />
            <button
              onClick={send}
              disabled={sending || !text.trim()}
              className="rounded-xl bg-gradient-gold text-black font-semibold px-5 py-3 text-sm disabled:opacity-40"
            >
              Send
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  // ── Conversation list ────────────────────────────────────────────────────────
  return (
    <AppShell>
      <div className="px-6 md:px-12 py-10 md:py-14">
        <h1 className="font-display text-4xl md:text-6xl mb-8">Messages</h1>

        {loading && <div className="text-white/60">Loading…</div>}

        {!loading && conversations.length === 0 && (
          <div className="glass rounded-3xl p-8 md:p-10 max-w-2xl">
            <h2 className="font-display text-2xl mb-3">No conversations yet</h2>
            <p className="text-white/60 mb-6">
              Conversations appear here once you have a match. Complete your journey to unlock messaging.
            </p>
            <Link
              to="/select-seven"
              className="inline-flex rounded-full bg-gradient-gold text-black font-semibold px-6 py-3"
            >
              Continue your journey
            </Link>
          </div>
        )}

        {!loading && conversations.length > 0 && (
          <div className="space-y-3 max-w-2xl">
            {conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveConvo(c)}
                className="w-full text-left glass rounded-2xl p-5 hover:ring-1 hover:ring-white/20 transition-all flex items-center gap-4"
              >
                {c.image ? (
                  <img src={c.image} alt="" className="h-12 w-12 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-white/10 grid place-items-center text-lg font-display flex-shrink-0">
                    {(c.name || "?")[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg">{c.name || "Conversation"}</div>
                  <div className="text-sm text-white/50 truncate">
                    {c.last_message || "No messages yet."}
                  </div>
                </div>
                <span className="text-white/30 text-sm">→</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
